//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type AxiosResponse } from 'axios';
import { type ApiClient } from './apiClient';
import { type AsyncStatusResponse } from './apiTypes';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Async-job infrastructure: poll a backend async job to completion and cancel a
 * running one.
 */
export class AsyncService {
  constructor(private readonly api: ApiClient) {}

  // See docs/AsyncMonitor.md for design of this method.
  asyncStatus = async <T = AsyncStatusResponse>(
    asyncID: string,
    wait: boolean,
    timeout: number,
    statusCallback: ((data: AsyncStatusResponse) => void) | null = null,
    abortSignal: AbortSignal | null = null
  ): Promise<T> => {
    // Constants.
    const POLL_INTERVAL_MS = 1000; // Check status every second.

    const request = `async_status?asyncID=${encodeURIComponent(asyncID)}`;

    if (wait) {
      // Waiting mode: Poll until job completes or is cancelled.
      const startTime = Date.now();

      // Make initial status request.
      let result =
        await this.api.makeRequest<AxiosResponse<AsyncStatusResponse>>(
          request, 'get', null, false, null, null, false, true);

      // Polling loop - continues while backend returns HTTP 202 (Accepted =
      // still processing).  Any other HTTP status code (200, 4xx, 5xx)
      // indicates completion.
      while(result.status === 202) {
        // Check if user clicked cancel button (frontend abort signal).  Returns
        // immediately with frontend cancellation response.  Job continues
        // running on server (backend cancellation happens separately).
        if (abortSignal && abortSignal.aborted) {
          console.log('Polling aborted by signal');
          return { error: 'Operation cancelled by user',
                   error_type: 'cancelled', is_error: true } as T;
        }

        // Invoke status callback for real-time UI updates.  Passes backend
        // status data (execution trace, progress, messages) to caller.
        if (statusCallback && result.data) {
          statusCallback(result.data);
        }

        // Check if backend job was cancelled via cancelAsyncJob() API call.
        // Backend sets error_type='cancelled' in response when job is
        // terminated.  This stops polling and returns the backend's
        // cancellation data.
        if (result.data && result.data.error_type === 'cancelled') {
          console.log('Operation cancelled, stopping polling');
          return result.data as T; // Return backend's cancellation response
        }

        // If timeout > 0, enforce maximum wait time.  timeout = 0 means wait
        // indefinitely (no timeout).  Throws error rather than returning to
        // indicate abnormal termination.
        if (timeout > 0 && (Date.now() - startTime) >= timeout) {
          throw new Error('Timeout reached while waiting for async status');
        }

        // Wait before next poll to avoid overwhelming the server.
        // Standard polling interval: 1 second (1000ms).
        await sleep(POLL_INTERVAL_MS);

        // Re-check abort signal after sleep period.
        // Handles race condition: user clicks cancel during the 1-second sleep.
        // Without this check, we'd make one more unnecessary backend request.
        // Most cancellations are caught here (between poll intervals).
        if (abortSignal && abortSignal.aborted) {
          console.log('Polling aborted by signal before next request');
          return { error: 'Operation cancelled by user',
                   error_type: 'cancelled', is_error: true } as T;
        }

        // Make next polling request to check job status.
        // Returns HTTP 202 if still processing, other codes if complete.
        result = await this.api.makeRequest<AxiosResponse<AsyncStatusResponse>>(
          request, 'get', null, false, null, null, false, true);
      }

      // Job completed (HTTP status != 202).
      // Return the final result data (cypher, explanation, error, etc.).
      return result.data as T;
    } else {
      // Non-waiting mode: Return immediately with the current job status.  Used
      // for one-time status checks without polling.  returnFullResponse stays
      // false so this resolves to the response data (T), matching the declared
      // Promise<T> — unlike the waiting branch above, which needs the full
      // response to read the 202 status.
      return this.api.makeRequest<T>(request, 'get', null, false, null, null,
                                     false, false);
    }
  };

  // See docs/CancelAsyncJob.md for design of this method.
  cancelAsyncJob = async (asyncID: string): Promise<void> => {
    await this.api.makeRequest(
      `cancel_async?asyncID=${encodeURIComponent(asyncID)}`, 'post',
      null, false, null, null, false, true);
  };
}
